<template>
  <q-dialog
    v-model="shown"
    persistent
  >
    <q-card
      v-if="order"
      style="min-width: 25%;"
    >
      <q-card-section class="row items-center q-mx-xs">
        <div class="text-h6">
          Claim Refund
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>

      <q-card-section>
        <div
          class="row justify-end q-gutter-sm q-mt-xs"
        >
          <div class="col-auto">
            Unclaimed
          </div>
          <i18n-n
            class="col-auto"
            tag="span"
            :value="refundAmountRemaining"
            format="currency"
            scope="global"
          />
        </div>
      </q-card-section>

      <q-card-actions
        class="row q-gutter-sm justify-end q-ma-sm items-start"
      >
        <div
          v-for="refundOption in refundOptions"
          :key="refundOption.id"
          class="col-auto column items-center"
        >
          <q-btn
            :color="refundOption.color"
            :disabled="refundOption.disabled || !refundAmountRemaining && !refundOption.amount"
            outline
            @click="selectRefundOption(refundOption)"
          >
            {{ refundOption.label || $_.startCase(refundOption.type) }}
            <q-chip
              v-if="refundOption.amount"
              dense
              size="sm"
            >
              <i18n-n
                :value="refundOption.amount"
                format="currency"
                scope="global"
              />
            </q-chip>
          </q-btn>
          <div
            v-if="refundOption.maxAmount"
            class="text-overline text-grey-8"
          >
            Up to
            <i18n-n
              :value="refundOption.maxAmount"
              format="currency"
              scope="global"
            />
          </div>
          <q-tooltip v-if="refundOption.tooltip">
            {{ refundOption.tooltip }}
          </q-tooltip>
        </div>
        <q-space />
        <q-btn
          color="primary"
          :disable="!!refundAmountRemaining"
          :loading="refunding"
          @click="claimRefund"
        >
          Refund
          <i18n-n
            v-if="refundAmount"
            class="q-ml-xs"
            tag="span"
            :value="refundAmount"
            format="currency"
            scope="global"
          />
        </q-btn>
      </q-card-actions>
    </q-card>
    <refund-edit-amount-dialog
      ref="editAmountDialog"
      :amount-remaining="refundAmountRemaining"
      @save="updateRefundAmount"
    />
  </q-dialog>
</template>

<script>
import { Dialog } from 'quasar'
import Payment from 'src/models/payment/Payment'
import RefundEditAmountDialog from 'src/modules/admin/order/RefundEditAmountDialog'

export default {
  name: 'UnclaimedRefundDialog',
  components: {
    RefundEditAmountDialog
  },
  data () {
    return {
      order: null,
      shown: false,
      refundOptions: [],
      refunding: false
    }
  },
  computed: {
    payments () {
      return this.order?.payments.data || []
    },
    refundAmount () {
      return Math.abs(_(this.payments).filter({ state: 'completed', type: 'unclaimed-refund' }).map('amount').sum())
    },
    refundAmountRemaining () {
      return (Math.round((this.refundAmount - _(this.refundOptions).map('amount').sum()) * 100) / 100)
    }
  },
  watch: {
    shown () {
      if (this.shown) {
        this.setRefundOptions()
      } else {
        Object.assign(this.$data, this.$options.data.apply(this))
      }
    }
  },
  configuration: ['paymentTypes'],
  methods: {
    show (order) {
      this.order = order
      this.shown = true
    },
    updateRefundAmount (amount, refundType) {
      refundType.amount = amount
    },
    setRefundOptions () {
      _(this.payments).filter({ state: 'draft' }).each(payment => this.order.payments.remove(payment))

      if (this.refundAmount <= 0) return

      this.order.payments.add(new Payment({
        type: 'unclaimed-refund',
        amount: this.refundAmount
      }))

      _(this.payments).filter(payment => payment.state === 'completed' && payment.amount > 0).each(payment => {
        if (payment.type === 'card') {
          // Support multiple partial refunds
          const maxAmount = _(this.order.payments.data).filter({ stripePaymentIntentId: payment.stripePaymentIntentId, state: 'completed' }).map('amount').sum()

          this.refundOptions.push({
            id: payment.id,
            type: 'card',
            subtype: payment.subtype,
            label: `Credit Card ...${payment.lastFour}`,
            color: 'primary',
            stripePaymentIntentId: payment.stripePaymentIntentId,
            lastFour: payment.lastFour,
            cardType: payment.cardType,
            nameOnCard: payment.nameOnCard,
            refundFromPaymentId: payment.id,
            amount: 0,
            maxAmount
          })
        }
        if (payment.type === 'cash') {
          this.refundOptions.push({
            id: 'cash',
            type: 'cash',
            amount: 0,
            color: 'green',
            disabled: !this.$register,
            tooltip: !this.$register ? 'Only available in Register App' : null
          })
        }
        if (payment.type === 'gift-card') {
          this.refundOptions.push({
            id: payment.id,
            type: 'gift-card',
            label: 'Gift Card',
            color: 'accent',
            giftCard: payment.giftCard,
            amount: 0
          })
        }
        if (payment.paymentTypeId) {
          const resolvedPaymentType = _.find(this.paymentTypes.data, { id: payment.paymentTypeId })
          if (!resolvedPaymentType) {
            this.refundOptions.push({
              id: payment.id,
              paymentTypeId: payment.paymentTypeId,
              label: payment.subtype + ' (Not Found)',
              color: 'warning',
              disabled: true,
              amount: 0
            })
          } else {
            this.refundOptions.push({
              id: payment.id,
              paymentTypeId: resolvedPaymentType.id,
              label: resolvedPaymentType.name,
              color: 'warning',
              disabled: !resolvedPaymentType.enabled,
              amount: 0
            })
          }
        }
      })

      // For orders that were a credit, don't allow cash, only taking credit back from user
      if (this.order.total > 0) {
        this.refundOptions.push({
          id: 'cash',
          type: 'cash',
          amount: 0,
          color: 'green'
        })
      }

      this.refundOptions.push({
        id: 'credit',
        type: 'credit',
        label: 'User Credit',
        amount: 0,
        color: 'positive',
        disabled: !this.order.user,
        tooltip: !this.order.user ? 'Only available for orders with an associated user' : null
      })
    },
    async selectRefundOption (refundOption) {
      // If this is the first time, just add it. If there is already an amount, edit it
      if (refundOption.amount) {
        this.$refs.editAmountDialog.show(refundOption)
      } else {
        refundOption.amount = Math.round((refundOption.maxAmount ? _.min([this.refundAmountRemaining, refundOption.maxAmount]) : this.refundAmountRemaining) * 100) / 100
      }
    },
    async claimRefund () {
      this.refunding = true

      const cashChangeDue = _(this.refundOptions).filter({ type: 'cash' }).map('amount').sum()
      const success = await this.order.claimRefund(_.filter(this.refundOptions, option => option.amount !== 0))
      this.refunding = false

      this.shown = false

      if (success && cashChangeDue > 0) {
        Dialog.create({
          title: 'Change',
          message: `Change Due: ${this.$i18n.n(cashChangeDue, 'currency')}`
        })
      }
    }
  }
}
</script>
